/* eslint-disable max-lines-per-function */
import {
  Grid,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { InputFile } from "../common/InputFile";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { IAlert } from "../../interfaces/alert";
import GenericAlert from "../GenericAlert";
import {
  ERROR,
  SUCCESS,
} from "../../utils/constants";
import { Button } from "../common/Button";
import {
  getCSVExample,
  getCSVStatus,
  updateOwnFleetOrdersByCSV,
} from "../../requests/orderRequests";
import { UserContext } from "../../contexts/userContext";
import {
  getUploadedCSVStatus,
  mongoTimeStampToString,
} from "../../utils/orders";
import {
  IGetUpdateOrderCsvDTO,
  IUpdateOrderCsvResponseDTO,
  IUpdateOrderStatusProps,
} from "../../interfaces/order";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { TCSVUploadStatus } from "../../interfaces/configuration";
import { csvStatusLabelMap } from "../../utils/configuration";

const CSVUploadStatusLabel = ({
  csvStatus,
}: {
  csvStatus: TCSVUploadStatus;
}) => {
  return (
    <Typography
      className="f-s-14 m-t-10"
      color={csvStatusLabelMap[csvStatus].color}
    >
      {csvStatusLabelMap[csvStatus].label}
    </Typography>
  );
};

const BulkOwnFleetOrderStatusUpdate = ({ onBackClick }: IUpdateOrderStatusProps) => {
  const { selectedMarketplace: marketplaceId } = useContext(UserContext);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [alert, setAlert] = useState<IAlert>({ showAlert: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvStatus, setCsvStatus] = useState<IUpdateOrderCsvResponseDTO>();
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
    }
  };

  const handleCancel = () => {
    setCsvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setAlert({ showAlert: false });
  };

  const handleSubmit = async () => {
    if (!csvFile) return;

    setAlert({ showAlert: false });
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("order_update_csv", csvFile);
    formData.append("marketplace_id", marketplaceId);

    try {
      await updateOwnFleetOrdersByCSV(formData);
      setAlert({
        typeOfAlert: SUCCESS,
        message: "El archivo fue cargado con éxito",
        showAlert: true,
      });
    } catch (error: any) {
      setAlert({
        typeOfAlert: ERROR,
        message: error,
        showAlert: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const callGetCSVStatus = async () => {
      try {
        const response: IGetUpdateOrderCsvDTO = await getCSVStatus(marketplaceId);
        setCsvStatus(response.results[0]);
      } catch (error) {
        console.error("Error fetching CSV status:", error);
      }
    };
    callGetCSVStatus();
  }, []);

  const hasCsvErrors =
    csvStatus?.processed && csvStatus.download_errors_csv_url;

  const handleDownloadErrors = () => {
    if (csvStatus?.download_errors_csv_url) {
      const link = document.createElement("a");
      link.href = csvStatus.download_errors_csv_url;
      link.download = "errores.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };


  const handleGetCSVSExample = async () => {
    try {
      const { url } = await getCSVExample();
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.download = "csv-ejemplo.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Grid container flexDirection={"column"}>
      <Grid item container alignItems={"center"}>
        <Grid item>
          <IconButton onClick={onBackClick}>
            <span className="material-symbols-rounded text-dark">
              keyboard_backspace
            </span>
          </IconButton>
        </Grid>
        <Grid item>
          <Typography className="f-s-20">Actualizar estados por CSV</Typography>
        </Grid>
      </Grid>
      {alert.showAlert && <GenericAlert alert={alert} />}
      <Grid item marginTop={"30px"} marginLeft={"10px"} xs={4}>
        <Typography className="f-s-18">Subí tu archivo CSV</Typography>
        <Typography marginTop="20px" className="f-s-14">
          Descargá la plantilla desde este{" "}
          <Link
            onClick={handleGetCSVSExample}
            target="_blank"
            rel="noreferrer"
            download="csv-ejemplo.csv"
            style={{ color: "#3979B5", cursor: "pointer" }}
          >
            enlace
          </Link>{" "}
          y completá los campos requeridos:{" "}
          <Tooltip title="suborder_id" placement="top">
            <span>ID de sub orden</span>
          </Tooltip>{" "}
          y{" "}
          <Tooltip title="delivered" placement="top">
            <span>entregado</span>
          </Tooltip>
          .
        </Typography>
        <Paper
          sx={{
            p: 1,
            border: "1px solid var(--muted)",
            boxShadow: 0,
          }}
          style={{
            marginTop: "30px",
          }}
        >
          <InputFile
            onChange={handleFileChange}
            inputProps={{
              accept: ".csv",
            }}
            inputRef={fileInputRef}
          >
            Importar CSV
          </InputFile>
        </Paper>
        {csvFile && (
          <Grid container className="m-t-40" columnSpacing={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        )}

        {csvStatus && (
          <Grid container flexDirection={"column"} className="m-t-20" gap={2}>
            <Typography className="f-s-14">
              <span className="text-bold">Fecha de carga:</span>{" "}
              {mongoTimeStampToString(csvStatus.created_at)}
            </Typography>
            <Grid display={"flex"} alignItems={"center"} gap={0.5}>
              <Typography className="f-s-14">
                <span className="text-bold">Estado de carga:</span>
              </Typography>
              <CSVUploadStatusLabel
                csvStatus={getUploadedCSVStatus(csvStatus)}
              />
            </Grid>
          </Grid>
        )}

        {hasCsvErrors && (
          <Button
            size="medium"
            variant="outlined"
            className="m-t-20"
            color="primary"
            onClick={handleDownloadErrors}
            endIcon={<FileDownloadOutlinedIcon />}
          >
            Descargar errores
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default BulkOwnFleetOrderStatusUpdate;
